import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const NewsletterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    acceptPolicy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const endpoint = '/contact.php';

      await axios.post(endpoint, {
        name: formData.email,
        email: formData.email,
        phone: '',
        subject: 'Inscription newsletter',
        message: `Nouvelle inscription à la newsletter depuis le site.\n\nEmail: ${formData.email}`
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });

      setSubmitStatus('success');
      setFormData({ email: '', acceptPolicy: false });
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Newsletter</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">Newsletter</h1>
          </div>
        </div>
      </div>

      {/* Newsletter Form */}
      <div className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Suivez notre actualité</h2>
            <p className="text-xl text-gray-600 mb-2">
              Abonnez-vous à la newsletter mensuelle de MRTP SARL
            </p>
            <p className="text-lg font-semibold text-gray-800">
              Et ne manquez aucune de notre actualité !
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 md:p-12">
            <p className="text-lg text-gray-600 mb-8 text-center">
              Laissez-vous <strong>inspirer</strong> et <strong>informer</strong> en recevant chaque mois notre newsletter : l'actualité de l'immobilier, interview clients, les événements de MRTP SARL, présentation de projets 3D, portrait de notre équipe : directement dans votre boite mail !
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse email *
                </label>
                <div className="relative">
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="votre@email.com"
                    className="w-full pl-12"
                  />
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="accept"
                  checked={formData.acceptPolicy}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptPolicy: checked })}
                />
                <label
                  htmlFor="accept"
                  className="text-sm text-gray-600 cursor-pointer"
                >
                  Je souhaite m'inscrire à la newsletter. J'accepte la{' '}
                  <Link to="/politique-confidentialite" className="text-[#c4d600] hover:underline">
                    politique de confidentialité
                  </Link>
                  .
                </label>
              </div>

              {submitStatus === 'success' && (
                <p className="text-center text-green-700 text-sm">
                  Votre inscription a été prise en compte. Merci !
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-center text-red-700 text-sm">
                  Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.
                </p>
              )}

              <Button
                type="submit"
                disabled={!formData.acceptPolicy || isSubmitting}
                className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Envoi en cours..." : "S'abonner à la newsletter"}
              </Button>
            </form>

            <div className="mt-8 text-center text-sm text-gray-500">
              <p>Vos données sont protégées et ne seront jamais partagées avec des tiers.</p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-gray-800" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Conseils exclusifs</h3>
              <p className="text-sm text-gray-600">Recevez nos meilleurs conseils en construction et aménagement</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-gray-800">📰</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Actualités</h3>
              <p className="text-sm text-gray-600">Restez informé de toutes nos nouveautés et événements</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-gray-800">💡</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Inspiration</h3>
              <p className="text-sm text-gray-600">Découvrez nos projets 3D et réalisations clients</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Back to Conseils */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Découvrez nos derniers articles</h3>
          <Button asChild variant="outline" className="border-[#c4d600] text-[#c4d600] hover:bg-[#c4d600] hover:text-gray-800">
            <Link to="/nos-conseils">Voir tous nos conseils</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPage;